import { Fragment, useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  findReferencesForCalibre,
  getCustomCalibres,
  getReferencesByDocIds,
  getWatchBrands,
  recomputeCalibreReferenceCountsFor,
  type CalibreReferenceCandidate,
  type CustomCalibreEntry,
} from '../../services/firebase';
import { getBrandIdsWithCalibreText, isAlgoliaConfigured } from '../../services/algolia';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import type { WatchBrand } from '../../types';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { type CalibreDualWriteFlags } from './calibreBulkUpdate';
import { pushCalibreDataToRefs } from './parentCalibrePush';
import {
  ALL_REAPPLY_SOURCES,
  calibreTextLookupNames,
  fieldBreakdownRows,
  forecastRefsColumn,
  groupCandidateIdsByBrand,
  reapplyScopeBrandIds,
  selectReapplyRefs,
  summariseBrandMatches,
  totalReapplySummary,
  verifyBrandCandidates,
  type ReapplyMatchedRef,
  type ReapplyMatchSource,
  type ReapplySources,
} from './reapplyCalibre';

/**
 * "Reapply to matching references" — the brand-side Calibre Enrichment tool run
 * from the calibre's end: find this calibre's references in every brand and
 * push its data onto them.
 *
 * Behaviour is the brand tool's, by construction. A read-only callable finds
 * candidates; `verifyBrandCandidates` keeps only the ones the brand tool's own
 * matcher assigns to this calibre; the writes are the brand tool's own
 * `pushCalibreDataToRefs`. There are no per-field controls because the brand
 * tool has none.
 */

interface BrandRow {
  brand: WatchBrand;
  matched: ReapplyMatchedRef[];
}

interface FindResults {
  /** The calibre AS STORED, read at Find time — what Apply pushes. */
  entry: CustomCalibreEntry;
  allCalibres: CustomCalibreEntry[];
  rows: BrandRow[];
  scope: 'usingBrands' | 'all';
  scanned: number;
  /** Candidates the brand rules gave to another calibre or to none. */
  rejected: number;
  /** Candidates under a brand id with no brand document. */
  orphaned: number;
}

interface ApplyOutcome {
  updated: number;
  fields: number;
  unchanged: number;
  failedBrands: { name: string; message: string }[];
  /** The calibre's Refs count after the closing recount; null if it did not run. */
  referenceCount: number | null;
  /** Why the closing recount did not produce a count, when it did not. */
  recountWarning: string | null;
}

const SOURCE_LABEL: Record<ReapplyMatchSource, string> = {
  link: 'Already linked (ID)',
  name: 'Exact name',
  alias: 'Alias',
};

const SCAN_CONCURRENCY = 4;

function errorMessage(err: unknown): string {
  const code = (err as { code?: string } | null)?.code ?? '';
  if (code === 'functions/not-found') {
    return 'The findReferencesForCalibre function is not deployed yet.';
  }
  return err instanceof Error ? err.message : String(err);
}

/** Every slice of one scan (a brand's, or the whole catalogue's). */
async function scanAll(
  calibreId: string,
  brandId: string | undefined,
  onProgress: (scanned: number) => void,
): Promise<CalibreReferenceCandidate[]> {
  const found: CalibreReferenceCandidate[] = [];
  let startAfterPath: string | undefined;
  for (;;) {
    const res = await findReferencesForCalibre({
      calibreId,
      mode: 'scan',
      ...(brandId ? { brandId } : {}),
      ...(startAfterPath ? { startAfterPath } : {}),
    });
    found.push(...res.candidates);
    onProgress(res.scanned);
    if (!res.nextCursor) return found;
    startAfterPath = res.nextCursor;
  }
}

interface ReapplyCalibreToReferencesProps {
  isOpen: boolean;
  onClose: () => void;
  calibreId: string;
  calibreName: string;
}

export function ReapplyCalibreToReferences({
  isOpen,
  onClose,
  calibreId,
  calibreName,
}: ReapplyCalibreToReferencesProps) {
  const dualWriteLegacyMovement = useFeatureFlag('calibre.dualWriteLegacyMovement');
  const dualWriteLegacyCalibreString = useFeatureFlag('calibre.dualWriteLegacyCalibreString');
  const flags = useMemo<CalibreDualWriteFlags>(
    () => ({
      legacyMovement: dualWriteLegacyMovement,
      legacyCalibreString: dualWriteLegacyCalibreString,
    }),
    [dualWriteLegacyMovement, dualWriteLegacyCalibreString],
  );

  const [sources, setSources] = useState<ReapplySources>(ALL_REAPPLY_SOURCES);
  const [allBrands, setAllBrands] = useState(false);
  // Parents and standalone references by default; variants are a second,
  // opt-in pass.
  const [includeVariants, setIncludeVariants] = useState(false);
  const [finding, setFinding] = useState(false);
  const [findStatus, setFindStatus] = useState('');
  const [results, setResults] = useState<FindResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyStatus, setApplyStatus] = useState('');
  const [outcome, setOutcome] = useState<ApplyOutcome | null>(null);
  // Set once an Apply has written: the table is then a local projection of
  // what was sent, not a read of Firestore, so Apply stays off until a rescan.
  const [appliedSinceFind, setAppliedSinceFind] = useState(false);

  const busy = finding || applying;

  const handleFind = useCallback(async () => {
    setFinding(true);
    setError(null);
    setResults(null);
    setOutcome(null);
    setAppliedSinceFind(false);
    setExpanded(new Set());
    const scope = allBrands ? 'all' : 'usingBrands';
    try {
      setFindStatus('Loading the calibre catalogue…');
      const [allCalibres, brands] = await Promise.all([getCustomCalibres(), getWatchBrands()]);
      const entry = allCalibres.find((c) => c.id === calibreId);
      if (!entry) throw new Error(`Calibre "${calibreId}" is not in the catalogue.`);

      let scanned = 0;
      const candidates: CalibreReferenceCandidate[] = [];
      if (scope === 'all') {
        setFindStatus('Scanning every brand…');
        candidates.push(
          ...(await scanAll(calibreId, undefined, (n) => {
            scanned += n;
            setFindStatus(`Scanning every brand… ${scanned.toLocaleString()} references read`);
          })),
        );
      } else {
        setFindStatus('Finding the brands with references to this calibre…');
        // Linked brands from Firestore; text-match brands from the search
        // index. The index is a hint — if it is unavailable the scope simply
        // falls back to the other three sources.
        const [linked, textMatchBrandIds] = await Promise.all([
          findReferencesForCalibre({ calibreId, mode: 'linked' }),
          isAlgoliaConfigured()
            ? getBrandIdsWithCalibreText(calibreTextLookupNames(entry)).catch((err) => {
                console.warn('[ReapplyCalibreToReferences] text-match brand lookup failed', err);
                return [] as string[];
              })
            : Promise.resolve([] as string[]),
        ]);
        const brandIds = reapplyScopeBrandIds({
          linkedBrandIds: linked.candidates.map((c) => c.brandId),
          entry,
          textMatchBrandIds,
        });
        let done = 0;
        const queue = [...brandIds];
        const worker = async () => {
          for (let id = queue.shift(); id; id = queue.shift()) {
            candidates.push(...(await scanAll(calibreId, id, (n) => { scanned += n; })));
            done += 1;
            setFindStatus(`Scanning brands… ${done} of ${brandIds.length}`);
          }
        };
        await Promise.all(Array.from({ length: Math.min(SCAN_CONCURRENCY, queue.length) }, worker));
      }

      setFindStatus('Checking candidates against the brand matching rules…');
      const brandById = new Map(brands.map((b) => [b.id, b]));
      const rows: BrandRow[] = [];
      let rejected = 0;
      let orphaned = 0;
      for (const [brandId, ids] of groupCandidateIdsByBrand(candidates)) {
        const brand = brandById.get(brandId);
        if (!brand) {
          orphaned += ids.length;
          continue;
        }
        const refs = Array.from((await getReferencesByDocIds(brandId, ids)).values());
        const verified = verifyBrandCandidates(calibreId, brand, refs, allCalibres);
        rejected += verified.rejected + (ids.length - refs.length);
        if (verified.matched.length > 0) rows.push({ brand, matched: verified.matched });
      }
      rows.sort((a, b) => b.matched.length - a.matched.length || a.brand.name.localeCompare(b.brand.name));
      setResults({ entry, allCalibres, rows, scope, scanned, rejected, orphaned });
    } catch (err) {
      console.error('[ReapplyCalibreToReferences] find failed', err);
      setError(errorMessage(err));
    } finally {
      setFinding(false);
      setFindStatus('');
    }
  }, [allBrands, calibreId]);

  const summaries = useMemo(
    () =>
      results
        ? results.rows.map((row) =>
            summariseBrandMatches(row.matched, results.entry, flags, sources, includeVariants),
          )
        : [],
    [results, flags, sources, includeVariants],
  );
  const total = useMemo(() => totalReapplySummary(summaries), [summaries]);
  const brandsSelected = summaries.filter((s) => s.selected > 0).length;
  // What this Apply does to the Refs column on Curated Calibres.
  const refsForecast = useMemo(
    () =>
      results
        ? forecastRefsColumn(
            results.rows.flatMap((row) => selectReapplyRefs(row.matched, sources, includeVariants)),
            results.entry,
            {
              unionSemantics: typeof results.entry.linkedReferenceCount === 'number',
              rewritesText: flags.legacyCalibreString,
            },
          )
        : null,
    [results, sources, includeVariants, flags],
  );

  const handleApply = useCallback(async () => {
    if (!results) return;
    setConfirming(false);
    setApplying(true);
    // One token for the whole Apply, as on the brand side.
    const runToken = `calibre-reapply-${crypto.randomUUID()}`;
    const done: ApplyOutcome = {
      updated: 0,
      fields: 0,
      unchanged: 0,
      failedBrands: [],
      referenceCount: null,
      recountWarning: null,
    };
    const nextRows: BrandRow[] = [];
    const targets = results.rows.filter(
      (row) => selectReapplyRefs(row.matched, sources, includeVariants).length > 0,
    );
    for (let i = 0; i < results.rows.length; i++) {
      const row = results.rows[i];
      const wanted = selectReapplyRefs(row.matched, sources, includeVariants);
      if (wanted.length === 0) {
        nextRows.push(row);
        continue;
      }
      setApplyStatus(`${row.brand.name} (${targets.indexOf(row) + 1} of ${targets.length})`);
      try {
        // Re-read and re-match at write time: the payload is built from the
        // documents as they are NOW, not as they were when Find ran.
        const fresh = await getReferencesByDocIds(row.brand.id, wanted.map((m) => m.ref.id));
        const verified = verifyBrandCandidates(
          calibreId,
          row.brand,
          Array.from(fresh.values()),
          results.allCalibres,
        );
        const toWrite = selectReapplyRefs(verified.matched, sources, includeVariants);
        const { updatedRefs, updatedCount, fieldsWritten } = await pushCalibreDataToRefs(
          row.brand.id,
          toWrite.map((m) => m.ref),
          results.entry,
          flags,
          runToken,
          // The per-write recount is deferred; ONE recount follows the loop.
          { deferCalibreRecount: true },
        );
        done.updated += updatedCount;
        done.fields += fieldsWritten;
        done.unchanged += toWrite.length - updatedCount;
        // Keep the table honest: a second Apply should report nothing to do.
        const updatedById = new Map(updatedRefs.map((r) => [r.id, r]));
        nextRows.push({
          ...row,
          matched: row.matched.map((m) => {
            const updated = updatedById.get(m.ref.id);
            return updated ? { ...m, ref: updated } : m;
          }),
        });
      } catch (err) {
        console.error('[ReapplyCalibreToReferences] apply failed for', row.brand.id, err);
        done.failedBrands.push({ name: row.brand.name, message: errorMessage(err) });
        nextRows.push(row);
      }
    }
    // The writes above told the count trigger to stand down, so this recount is
    // the only thing that updates the calibre's Refs number. Run it even when a
    // brand failed — the others were written.
    if (done.updated > 0) {
      setApplyStatus('Recounting references…');
      try {
        const recount = await recomputeCalibreReferenceCountsFor([calibreId]);
        const count = recount.counts[calibreId];
        if (typeof count === 'number') done.referenceCount = count;
        else if (recount.errors.length > 0) done.recountWarning = recount.errors[0].error;
        else if (recount.superseded === 0) done.recountWarning = 'the recount returned no count';
      } catch (err) {
        console.error('[ReapplyCalibreToReferences] recount failed', err);
        done.recountWarning =
          (err as { code?: string } | null)?.code === 'functions/not-found'
            ? 'the recomputeCalibreReferenceCounts function is not deployed yet'
            : errorMessage(err);
      }
    }
    setResults({ ...results, rows: nextRows });
    setOutcome(done);
    setAppliedSinceFind(true);
    setApplying(false);
    setApplyStatus('');
    if (done.failedBrands.length > 0) {
      toast.error(`Applied to ${done.updated} reference(s); ${done.failedBrands.length} brand(s) failed.`);
    } else {
      toast.success(`Applied calibre data to ${done.updated} reference(s).`);
    }
  }, [results, sources, includeVariants, flags, calibreId]);

  const toggleExpanded = (brandId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(brandId)) next.delete(brandId);
      else next.add(brandId);
      return next;
    });

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!busy && !confirming) onClose(); }}
      title={`Reapply "${calibreName}" to matching references`}
      size='full'
      dismissOnBackdrop={false}
    >
      <div className='space-y-4 text-sm'>
        <div className='flex flex-wrap items-center gap-x-6 gap-y-2'>
          <div className='flex items-center gap-3'>
            <span className='text-gray-600'>Match by</span>
            {(['link', 'name', 'alias'] as const).map((source) => (
              <label key={source} className='inline-flex items-center gap-1.5'>
                <input
                  type='checkbox'
                  checked={sources[source]}
                  disabled={busy}
                  onChange={(e) => setSources((prev) => ({ ...prev, [source]: e.target.checked }))}
                  className='rounded text-brand-600 focus:ring-brand-500'
                />
                {SOURCE_LABEL[source]}
              </label>
            ))}
          </div>
          <label
            className='inline-flex items-center gap-1.5'
            title="Off: brands with references to this calibre — linked by ID, matching its name or an alias, the calibre's own brand, and brands its aliases are scoped to. On: every brand — reads every reference once and can take a few minutes; use it to catch unusual spellings."
          >
            <input
              type='checkbox'
              checked={allBrands}
              disabled={busy}
              onChange={(e) => setAllBrands(e.target.checked)}
              className='rounded text-brand-600 focus:ring-brand-500'
            />
            All brands
            <span className='text-gray-500'>
              {allBrands ? '(scans every reference)' : '(off: brands with references to this calibre)'}
            </span>
          </label>
          <label
            className='inline-flex items-center gap-1.5'
            title='Off: parents and standalone references only. On: variants that match are applied too. Variants are never counted in the Refs column.'
          >
            <input
              type='checkbox'
              checked={includeVariants}
              disabled={busy}
              onChange={(e) => setIncludeVariants(e.target.checked)}
              className='rounded text-brand-600 focus:ring-brand-500'
            />
            Also apply to variants
            {results && <span className='text-gray-500'>({total.variants.toLocaleString()} matched)</span>}
          </label>
          <Button variant='secondary' size='sm' onClick={() => void handleFind()} disabled={busy}>
            {finding ? 'Finding…' : results ? 'Find again' : 'Find references'}
          </Button>
        </div>

        <details className='rounded-lg border border-gray-200 bg-gray-50 px-3 py-2'>
          <summary className='cursor-pointer text-gray-700'>What will be written</summary>
          <ul className='mt-2 list-disc space-y-1 pl-5 text-gray-600'>
            <li>
              <strong>Replaced with the calibre's value:</strong> the calibre name, movement type, base
              movement, the feature and certification flags, and the functions list.
            </li>
            <li>
              <strong>Filled only where the reference is empty:</strong> jewels, frequency, power reserve,
              dimensions and the other movement specs, and the complication lists.
            </li>
            <li>
              A name or alias match is also <strong>linked</strong> to this calibre. A tier that no longer
              exists on the calibre is cleared.
            </li>
            <li>Anything the calibre itself leaves blank is left alone on the reference.</li>
            <li>
              Identical to Calibre Enrichment on a brand — there are no per-field choices. The calibre is
              applied as last saved.
            </li>
          </ul>
        </details>

        {finding && <p className='text-gray-600'>{findStatus}</p>}
        {error && (
          <p role='alert' className='rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700'>
            {error}
          </p>
        )}

        {results && (
          <>
            <p className='text-gray-700'>
              <strong>{total.selected.toLocaleString()}</strong> reference{total.selected === 1 ? '' : 's'} in{' '}
              <strong>{brandsSelected}</strong> brand{brandsSelected === 1 ? '' : 's'}
              {' · '}
              {results.scope === 'all' ? 'all brands scanned' : 'brands with references to this calibre'}
              {results.rejected > 0 && (
                <span
                  title='Found by text or link, but the brand matching rules give them to another calibre or to none (linked elsewhere, another calibre owns that name, or the alias is not offered to that brand).'
                >
                  {' · '}
                  {results.rejected.toLocaleString()} candidate{results.rejected === 1 ? '' : 's'} not matched by
                  the brand rules
                </span>
              )}
              {results.orphaned > 0 && (
                <span>
                  {' · '}
                  {results.orphaned.toLocaleString()} under a brand that no longer exists
                </span>
              )}
            </p>

            {results.rows.length === 0 ? (
              <p className='rounded-lg border border-gray-200 px-3 py-6 text-center text-gray-500'>
                {results.scope === 'usingBrands'
                  ? 'No references matched in the brands that link to this calibre, own it, or store its name or an alias. Tick "All brands" to catch unusual spellings elsewhere.'
                  : 'No references matched this calibre in any brand.'}
              </p>
            ) : (
              <div className='overflow-x-auto rounded-lg border border-gray-200'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
                    <tr>
                      <th className='px-3 py-2 text-left font-medium'>Brand</th>
                      <th className='px-3 py-2 text-right font-medium'>Linked</th>
                      <th className='px-3 py-2 text-right font-medium'>Name</th>
                      <th className='px-3 py-2 text-right font-medium'>Alias</th>
                      <th className='px-3 py-2 text-right font-medium'>Variants</th>
                      <th className='px-3 py-2 text-right font-medium'>Would change</th>
                      <th className='px-3 py-2 text-right font-medium'>Fields</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100'>
                    {results.rows.map((row, i) => {
                      const s = summaries[i];
                      const open = expanded.has(row.brand.id);
                      const dim = (source: ReapplyMatchSource) => (sources[source] ? '' : 'text-gray-300 line-through');
                      return (
                        <Fragment key={row.brand.id}>
                          <tr>
                            <td className='px-3 py-2'>
                              <button
                                type='button'
                                onClick={() => toggleExpanded(row.brand.id)}
                                aria-expanded={open}
                                className='text-left font-medium text-gray-900 hover:text-brand-700'
                              >
                                <span className='mr-1 inline-block w-3 text-gray-400'>{open ? '▾' : '▸'}</span>
                                {row.brand.displayName || row.brand.name}
                              </button>
                            </td>
                            <td className={`px-3 py-2 text-right tabular-nums ${dim('link')}`}>{s.link}</td>
                            <td className={`px-3 py-2 text-right tabular-nums ${dim('name')}`}>{s.name}</td>
                            <td className={`px-3 py-2 text-right tabular-nums ${dim('alias')}`}>{s.alias}</td>
                            <td
                              className={`px-3 py-2 text-right tabular-nums ${includeVariants ? '' : 'text-gray-300'}`}
                              title={includeVariants ? 'Included in the counts to the left' : 'Not included — tick "Also apply to variants"'}
                            >
                              {s.variants}
                            </td>
                            <td className='px-3 py-2 text-right tabular-nums'>{s.wouldChange}</td>
                            <td
                              className='px-3 py-2 text-right tabular-nums'
                              title={fieldBreakdownRows(s.fieldCounts)
                                .map((r) => `${r.field}: ${r.count}`)
                                .join('\n')}
                            >
                              {s.fields}
                            </td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={7} className='bg-gray-50 px-3 py-2'>
                                <table className='min-w-full text-xs'>
                                  <thead className='text-gray-500'>
                                    <tr>
                                      <th className='py-1 pr-3 text-left font-medium'>Reference</th>
                                      <th className='py-1 pr-3 text-left font-medium'>Model</th>
                                      <th className='py-1 pr-3 text-left font-medium'>Stored calibre</th>
                                      <th className='py-1 text-left font-medium'>Matched by</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {row.matched.map((m) => (
                                      <tr
                                        key={m.ref.id}
                                        className={
                                          sources[m.source] && (includeVariants || !m.isVariant) ? '' : 'text-gray-400'
                                        }
                                      >
                                        <td className='py-0.5 pr-3 font-mono'>{m.ref.reference || m.ref.id}</td>
                                        <td className='py-0.5 pr-3'>{m.ref.model || '—'}</td>
                                        <td className='py-0.5 pr-3'>{m.storedCalibre || '—'}</td>
                                        <td className='py-0.5'>
                                          {SOURCE_LABEL[m.source]}
                                          {m.isVariant ? ' · variant' : ''}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot className='bg-gray-50 font-medium'>
                    <tr>
                      <td className='px-3 py-2'>Total</td>
                      <td className='px-3 py-2 text-right tabular-nums'>{total.link}</td>
                      <td className='px-3 py-2 text-right tabular-nums'>{total.name}</td>
                      <td className='px-3 py-2 text-right tabular-nums'>{total.alias}</td>
                      <td className='px-3 py-2 text-right tabular-nums'>{total.variants}</td>
                      <td className='px-3 py-2 text-right tabular-nums'>{total.wouldChange}</td>
                      <td className='px-3 py-2 text-right tabular-nums'>{total.fields}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </>
        )}

        {results && total.fields > 0 && (
          <details className='rounded-lg border border-gray-200 px-3 py-2'>
            <summary className='cursor-pointer text-gray-700'>
              Fields to be written ({total.fields.toLocaleString()} across{' '}
              {total.wouldChange.toLocaleString()} reference{total.wouldChange === 1 ? '' : 's'})
            </summary>
            <table className='mt-2 text-xs'>
              <thead className='text-gray-500'>
                <tr>
                  <th className='py-1 pr-6 text-left font-medium'>Field</th>
                  <th className='py-1 text-right font-medium'>References</th>
                </tr>
              </thead>
              <tbody>
                {fieldBreakdownRows(total.fieldCounts).map((row) => (
                  <tr key={row.field}>
                    <td className='py-0.5 pr-6 font-mono'>{row.field}</td>
                    <td className='py-0.5 text-right tabular-nums'>{row.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}

        {applying && <p className='text-gray-600'>Applying… {applyStatus}</p>}
        {outcome && (
          <div
            role='status'
            className={`rounded-lg border px-3 py-2 ${
              outcome.failedBrands.length > 0 || outcome.recountWarning
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-green-200 bg-green-50 text-green-800'
            }`}
          >
            Updated {outcome.updated.toLocaleString()} reference{outcome.updated === 1 ? '' : 's'} (
            {outcome.fields.toLocaleString()} fields); {outcome.unchanged.toLocaleString()} already matched the
            calibre.
            {outcome.referenceCount !== null && (
              <>
                {' '}
                Refs count is now <strong>{outcome.referenceCount.toLocaleString()}</strong> (parent and
                standalone references; variants are not counted).
              </>
            )}
            {outcome.recountWarning && (
              <p className='mt-1 font-medium text-amber-800'>
                The Refs count was not refreshed ({outcome.recountWarning}). Run "Recompute calibre ref
                counts" in Configuration to bring it up to date.
              </p>
            )}
            {outcome.failedBrands.length > 0 && (
              <ul className='mt-1 list-disc pl-5'>
                {outcome.failedBrands.map((f) => (
                  <li key={f.name}>
                    {f.name}: {f.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className='flex items-center justify-end gap-2 border-t pt-3'>
          {refsForecast && !appliedSinceFind && (
            <span
              className='mr-auto text-gray-600'
              title='The Refs column on Curated Calibres counts parent and standalone references linked by ID or matched by name/alias. Variants are never counted.'
            >
              Refs column currently: <strong>{refsForecast.current.toLocaleString()}</strong>.{' '}
              {refsForecast.after === refsForecast.current ? (
                'After apply: no change.'
              ) : (
                <>
                  After apply: <strong>{refsForecast.after.toLocaleString()}</strong>.
                </>
              )}
            </span>
          )}
          <Button variant='secondary' size='sm' onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button
            variant='primary'
            size='sm'
            onClick={() => setConfirming(true)}
            disabled={busy || !results || appliedSinceFind || total.wouldChange === 0}
            title={
              appliedSinceFind
                ? 'Click "Find again" to re-read the references before applying again.'
                : results && total.wouldChange === 0
                  ? 'Every matched reference already carries this calibre\'s data.'
                  : undefined
            }
          >
            {appliedSinceFind
              ? 'Applied — find again to rescan'
              : results && total.wouldChange === 0
                ? 'Nothing to apply'
                : `Apply to ${total.selected.toLocaleString()} reference${total.selected === 1 ? '' : 's'}…`}
          </Button>
        </div>
      </div>
    </Modal>

      <Modal
        isOpen={isOpen && confirming}
        onClose={() => setConfirming(false)}
        title='Apply calibre data?'
        size='md'
      >
        <div className='space-y-3 text-sm text-gray-700'>
          <p>
            This applies <strong>{calibreName}</strong> to{' '}
            <strong>
              {total.selected.toLocaleString()} reference{total.selected === 1 ? '' : 's'}
            </strong>{' '}
            in{' '}
            <strong>
              {brandsSelected} brand{brandsSelected === 1 ? '' : 's'}
            </strong>
            .
          </p>
          <ul className='list-disc space-y-1 pl-5'>
            <li>
              {sources.link ? total.link.toLocaleString() : 0} already linked ·{' '}
              {sources.name ? total.name.toLocaleString() : 0} by exact name ·{' '}
              {sources.alias ? total.alias.toLocaleString() : 0} by alias
            </li>
            <li>
              {includeVariants
                ? `Variants are included (${total.variants.toLocaleString()} matched).`
                : `Parents and standalone references only — ${total.variants.toLocaleString()} matching variant${total.variants === 1 ? ' is' : 's are'} left alone.`}
            </li>
            <li>
              <strong>{total.wouldChange.toLocaleString()}</strong> will be written (
              {total.fields.toLocaleString()} fields); the other{' '}
              {(total.selected - total.wouldChange).toLocaleString()} already match the calibre.
            </li>
            {(sources.name && total.name > 0) || (sources.alias && total.alias > 0) ? (
              <li>Name and alias matches will be linked to this calibre and their calibre text rewritten.</li>
            ) : null}
            <li>
              The calibre name, movement type, base movement, flags and functions on these references are{' '}
              <strong>overwritten</strong>. This cannot be undone from here.
            </li>
          </ul>
          <div className='flex justify-end gap-2 pt-2'>
            <Button variant='secondary' size='sm' onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant='primary' size='sm' onClick={() => void handleApply()}>
              Apply to {total.selected.toLocaleString()} reference{total.selected === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
